import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { articleApiClient } from '@src/api/tenant/article/articleApiClient';
import { Article } from '@src/api/types/article';
import { Action, TextLink } from '@src/ui/elements/button';
import { TagGray } from '@src/ui/elements/form';
import { forwardRef, useCallback, useEffect, useState } from 'react';

import { RelatedArticleModal } from '../RelatedArticleModal';

const MAX_RELATED_ARTICLES = 3;

interface RelatedArticlesProps {
  setRelatedArticles: (articles: Article[]) => void;
  initialValue?: Article[];
  currentArticle?: Article;
}

const RelatedArticles = forwardRef<HTMLDivElement, RelatedArticlesProps>(
  ({ setRelatedArticles, initialValue, currentArticle }, ref) => {
    const [articles, setArticles] = useState<Article[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedArticles, setSelectedArticles] = useState<Article[]>(initialValue || []);
    const [tempCheckedItems, setTempCheckedItems] = useState<Set<number>>(new Set());

    const fetchArticles = useCallback(async () => {
      const res = await articleApiClient.suggest();
      handleApiResponse(res, (data) => {
        setArticles(data);
      });
    }, []);

    useEffect(() => {
      fetchArticles();
    }, [fetchArticles]);

    useEffect(() => {
      if (initialValue?.length) {
        setSelectedArticles(initialValue);
        setRelatedArticles(initialValue);
      }
    }, [initialValue, setRelatedArticles]);

    const handleModalOpen = useCallback(() => {
      setTempCheckedItems(new Set(selectedArticles.map((article) => article.id)));
      setIsModalOpen(true);
    }, [selectedArticles]);

    const handleModalClose = useCallback(() => {
      setIsModalOpen(false);
    }, []);

    const handleToggleItem = useCallback((id: number) => {
      setTempCheckedItems((prev) => {
        const updated = new Set(prev);
        if (updated.has(id)) {
          updated.delete(id);
        } else if (prev.size < MAX_RELATED_ARTICLES) {
          updated.add(id);
        }
        return updated;
      });
    }, []);

    const handleApplyChanges = useCallback(
      (selectedItems: Article[]) => {
        setSelectedArticles(selectedItems);
        setRelatedArticles(selectedItems);
        handleModalClose();
      },
      [handleModalClose, setRelatedArticles],
    );

    const handleRemoveArticle = useCallback(
      (id: number) => {
        setSelectedArticles((prev) => {
          const updated = prev.filter((item) => item.id !== id);
          setRelatedArticles(updated);
          return updated;
        });
      },
      [setRelatedArticles],
    );

    const renderContent = () => {
      if (selectedArticles.length === 0) {
        return (
          <>
            <Action
              text='関連記事を選択'
              onClick={handleModalOpen}
            />
            <div className='flex justify-between'>
              <div />
              <p className='text-xs'>0/{MAX_RELATED_ARTICLES}</p>
            </div>
          </>
        );
      }

      return (
        <>
          <div className='placeholder:text-custom rounded border border-solid border-gray-250 bg-white p-4'>
            <div className='flex flex-wrap gap-[10px]'>
              {selectedArticles.map((item) => (
                <TagGray
                  key={item.id}
                  text={item.title}
                  index={item.id}
                  onClick={handleRemoveArticle}
                />
              ))}
            </div>
          </div>
          <div className='flex justify-between'>
            <TextLink
              text='関連記事を変更する'
              onClick={handleModalOpen}
            />
            <p className='text-xs'>
              {selectedArticles.length}/{MAX_RELATED_ARTICLES}
            </p>
          </div>
        </>
      );
    };

    return (
      <div
        ref={ref}
        className='flex flex-col gap-[6px]'
      >
        <p className='text-sm'>関連記事</p>
        {renderContent()}
        <RelatedArticleModal
          isOpen={isModalOpen}
          items={articles}
          checkedItems={tempCheckedItems}
          onToggle={handleToggleItem}
          onApply={handleApplyChanges}
          onClose={handleModalClose}
          currentArticle={currentArticle}
        />
      </div>
    );
  },
);

RelatedArticles.displayName = 'RelatedArticles';

export { RelatedArticles };
